import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { DownloadCloud } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Brandmark } from '@/components/layout/Brandmark';
import { useUpdateStore } from '@/stores/updateStore';

/**
 * Non-dismissible full-screen blocker for a mandatory update
 * (`X-App-Update-Force: true`). Rendered above LockScreen (z-[95] > z-[90]) —
 * a stale/insecure build must block even the biometric lock screen — and
 * mounted unconditionally at App root, since the update signal can arrive on
 * the very first pre-login response of a session (see useMe.ts).
 */
export function ForceUpdateScreen() {
  const { t } = useTranslation();
  const latestVersion = useUpdateStore((s) => s.latestVersion);
  const downloadUrl = useUpdateStore((s) => s.downloadUrl);

  if (typeof document === 'undefined') return null;

  return createPortal(
    <div className="fixed inset-0 z-[95] flex items-center justify-center bg-background/95 p-6 backdrop-blur-md">
      <div className="w-full max-w-sm space-y-6 rounded-lg border border-border bg-card p-8 shadow-ticket-hover">
        <div className="flex flex-col items-center gap-3 text-center">
          <Brandmark className="text-primary" size={40} />
          <div>
            <div className="label-tracked">{t('update.forceTitle')}</div>
            <h2 className="mt-1 font-mono text-2xl font-semibold leading-tight text-foreground">
              {latestVersion ?? ''}
            </h2>
          </div>
        </div>

        <p className="text-center text-sm text-muted-foreground">{t('update.forceDescription')}</p>

        <div className="dotted-rule" />

        <Button asChild size="lg" className="w-full">
          <a href={downloadUrl ?? '#'} target="_blank" rel="noreferrer">
            <DownloadCloud className="mr-2 h-4 w-4" />
            {t('update.downloadInstall')}
          </a>
        </Button>
      </div>
    </div>,
    document.body,
  );
}
